import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { AlertCircle, Building2, Clock, CreditCard, Hand, Info, SkipForward, Sparkles, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  CADENCES,
  CADENCE_LABEL,
  CARD_ART,
  daysText,
  formatDay,
  lastDay,
  money,
  parseDay,
  periodName,
  plural,
  shortDay,
  suggestedAccount,
  templateCreditValueCents,
  urgency,
  type BenefitKind,
  type Cadence,
  type CardTemplate,
  type PerkBenefit,
  type PerkCard,
  type PerkCreditAccount,
  type PerkItem,
  type PerkUnmatchedCredit,
  type PerksState,
  type ResetBasis,
} from '@/lib/perks'
import { errorMessage, perksApi, usePerksCatalog, usePerksMutation } from '@/lib/perksApi'
import { CardArt } from './PerkVisuals'
import { useBenefitActions } from './useBenefitActions'

/** "12.95" / "$1,200" → cents, or null. */
function parseCents(value: string): number | null {
  const cleaned = value.replace(/[^0-9.]/g, '')
  if (!cleaned) return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? Math.round(n * 100) : null
}

function longDay(value: string): string {
  return parseDay(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const NONE = '__none__'

// MARK: - Benefit detail

export function BenefitDialog({
  item,
  onOpenChange,
  onEdit,
}: {
  item: PerkItem | null
  onOpenChange: (open: boolean) => void
  onEdit: (card: PerkCard, benefit: PerkBenefit) => void
}) {
  const mutate = usePerksMutation()
  const { markUsed, skip } = useBenefitActions()
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(formatDay(new Date()))
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const benefitId = item?.benefit.id
  useEffect(() => {
    if (item) {
      setAmount(item.status.remainingCents ? String(item.status.remainingCents / 100) : '')
      setDate(formatDay(new Date()))
      setNote('')
    }
    // Reset only when a different benefit opens, not on every refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benefitId])

  if (!item) return null
  const { benefit, card, status } = item
  const open = !status.resolved
  const certificate = benefit.kind === 'certificate'
  const tone = open ? urgency(item.daysLeft) : 'calm'

  const logUse = async (event: FormEvent) => {
    event.preventDefault()
    const cents = parseCents(amount)
    if (!cents || cents <= 0) {
      toast.error('Enter the amount you used.')
      return
    }
    if (date > formatDay(new Date())) {
      toast.error('That date is in the future.')
      return
    }
    setSaving(true)
    try {
      await mutate(() => perksApi.logUse(benefit.id, { amount_cents: cents, date, note: note.trim() }))
      toast.success(`Logged ${money(cents)}`)
      const left = item.status.remainingCents - cents
      setAmount(left > 0 ? String(left / 100) : '')
      setNote('')
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const toggleEnrolled = async (value: boolean) => {
    try {
      await mutate(() => perksApi.updateBenefit(benefit.id, { is_enrolled: value }))
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  const removeUsage = async (id: number, detected: boolean) => {
    if (detected && !window.confirm('Remove this statement credit from the benefit? You can assign it elsewhere from Card Perks.')) return
    try {
      await mutate(() => perksApi.deleteUsage(id))
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  let caption: string
  if (certificate) caption = open ? `Use by ${shortDay(lastDay(status.period))} · ${daysText(item.daysLeft)} left` : `Next one arrives ${shortDay(status.period.end)}`
  else if (status.skipped) caption = `Skipped · resets ${shortDay(status.period.end)}`
  else if (open) caption = `of ${money(status.totalCents)} · ${shortDay(status.period.start)} – ${shortDay(lastDay(status.period))} · ${daysText(item.daysLeft)} left`
  else caption = `All ${money(status.totalCents)} used · resets ${shortDay(status.period.end)}`

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <CardArt art={card.art} mini />
            <span className="text-sm text-gray-500">{card.name}</span>
          </div>
          <DialogTitle className="text-xl">{benefit.name}</DialogTitle>
          <DialogDescription className="sr-only">Usage and history for {benefit.name}</DialogDescription>
        </DialogHeader>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {certificate ? (open ? 'Available' : 'Used') : `Left ${periodName(benefit.cadence, benefit.basis)}`}
          </div>
          {!certificate && <div className="mt-1 text-4xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">{money(status.remainingCents)}</div>}
          <div className={cn('mt-1 text-sm font-medium', tone === 'urgent' ? 'text-danger' : tone === 'soon' ? 'text-warning' : 'text-gray-500')}>{caption}</div>
        </div>

        {open && (
          <div className="space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-800">
            {!certificate && (
              <form onSubmit={logUse} className="grid grid-cols-[1fr_1fr] gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="perk-amount">Amount used</Label>
                  <Input id="perk-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="perk-date">Date</Label>
                  <Input id="perk-date" type="date" value={date} max={formatDay(new Date())} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="col-span-2 grid gap-1.5">
                  <Label htmlFor="perk-note">Note</Label>
                  <Input id="perk-note" placeholder="Optional" value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
                <Button type="submit" className="col-span-2" disabled={saving}>{saving ? 'Saving…' : 'Log a use'}</Button>
              </form>
            )}
            <div className="flex flex-wrap gap-2">
              <Button variant={certificate ? 'default' : 'outline'} size="sm" onClick={() => markUsed(item)}>
                {certificate ? 'Mark as used' : `Used all ${money(status.remainingCents)}`}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => skip(item)}>
                <SkipForward className="mr-1.5 h-4 w-4" />
                Skip {periodName(benefit.cadence, benefit.basis)}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          {item.waitingOn && (
            <Note icon={item.creditLooksMissing ? AlertCircle : Clock} danger={item.creditLooksMissing}>
              {item.creditLooksMissing
                ? `You spent ${money(item.waitingOn.amount_cents)} at ${item.waitingOn.name} on ${shortDay(parseDay(item.waitingOn.date))}, but no credit has posted. Check that the benefit is enrolled, or call the issuer.`
                : `Purchase at ${item.waitingOn.name} on ${shortDay(parseDay(item.waitingOn.date))}. The credit usually posts within a few days.`}
            </Note>
          )}
          {benefit.auto_detect && (
            <Note icon={Sparkles}>Picked up automatically from statement credits{card.mask ? ` on •• ${card.mask}` : ''}.</Note>
          )}
          {benefit.detail && <Note icon={Info}>{benefit.detail}</Note>}
        </div>

        {benefit.requires_enrollment && (
          <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800/50">
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">Enrolled</div>
              <div className="text-sm text-gray-500">Pays out only after you enroll with {card.issuer || 'the issuer'}.</div>
            </div>
            <Switch checked={benefit.is_enrolled} onCheckedChange={toggleEnrolled} aria-label="Enrolled" />
          </div>
        )}

        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">History</div>
          {benefit.usages.length === 0 && <p className="text-sm text-gray-500">Nothing logged yet.</p>}
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {benefit.usages.slice(0, 24).map((u) => {
              const detected = u.source === 'bank'
              const Icon = detected ? Building2 : u.is_skip ? SkipForward : Hand
              return (
                <li key={u.id} className="group flex items-center gap-3 py-2">
                  <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{longDay(u.date)}</div>
                    <div className="truncate text-xs text-gray-500">
                      {u.is_skip ? 'Skipped' : detected ? u.transaction_name ?? 'Statement credit' : u.note || 'Logged by you'}
                    </div>
                  </div>
                  {!u.is_skip && <div className="text-sm font-medium tabular-nums text-success">{money(u.amount_cents)}</div>}
                  <button
                    type="button"
                    onClick={() => removeUsage(u.id, detected)}
                    className="rounded p-1 text-gray-400 opacity-0 hover:text-danger focus-visible:opacity-100 group-hover:opacity-100"
                    aria-label={u.is_skip ? 'Undo skip' : 'Remove entry'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onEdit(card, benefit)}>Edit benefit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Note({ icon: Icon, danger = false, children }: { icon: typeof Info; danger?: boolean; children: React.ReactNode }) {
  return (
    <div className={cn('flex gap-2', danger && 'text-danger')}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  )
}

// MARK: - Benefit form

export function BenefitFormDialog({
  target,
  onOpenChange,
}: {
  target: { card: PerkCard; benefit: PerkBenefit | null } | null
  onOpenChange: (open: boolean) => void
}) {
  const mutate = usePerksMutation()
  const [name, setName] = useState('')
  const [kind, setKind] = useState<BenefitKind>('credit')
  const [amount, setAmount] = useState('')
  const [cadence, setCadence] = useState<Cadence>('quarterly')
  const [basis, setBasis] = useState<ResetBasis>('calendar')
  const [enrollment, setEnrollment] = useState(false)
  const [reminders, setReminders] = useState(true)
  const [detail, setDetail] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const b = target?.benefit
    setName(b?.name ?? '')
    setKind(b?.kind ?? 'credit')
    setAmount(b ? String(b.amount_cents / 100) : '')
    setCadence(b?.cadence ?? 'quarterly')
    setBasis(b?.basis ?? 'calendar')
    setEnrollment(b?.requires_enrollment ?? false)
    setReminders(b?.reminders_on ?? true)
    setDetail(b?.detail ?? '')
  }, [target])

  if (!target) return null
  const { card, benefit } = target

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim()) return toast.error('Give the benefit a name.')
    const payload: Record<string, unknown> = {
      name: name.trim(), kind, detail: detail.trim(), cadence,
      requires_enrollment: enrollment, reminders_on: reminders,
    }
    if (kind === 'credit') {
      const cents = parseCents(amount)
      if (!cents || cents <= 0) return toast.error('Enter how much the credit is worth each period.')
      payload.amount_cents = cents
      payload.basis = basis
    } else {
      payload.amount_cents = 0
      payload.basis = 'cardmember_year'
    }
    setSaving(true)
    try {
      await mutate(() => (benefit ? perksApi.updateBenefit(benefit.id, payload) : perksApi.createBenefit(card.id, payload)))
      onOpenChange(false)
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!benefit || !window.confirm(`Delete ${benefit.name}? Its history is deleted too.`)) return
    try {
      await mutate(() => perksApi.deleteBenefit(benefit.id))
      onOpenChange(false)
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{benefit ? 'Edit Benefit' : 'New Benefit'}</DialogTitle>
          <DialogDescription>{card.name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="benefit-name">Name</Label>
            <Input id="benefit-name" placeholder="e.g. Resy dining" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <Segmented
            value={kind}
            onChange={(v) => setKind(v as BenefitKind)}
            options={[{ value: 'credit', label: 'Dollar credit' }, { value: 'certificate', label: 'Certificate' }]}
          />
          {kind === 'credit' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="benefit-amount">Amount each period</Label>
                <Input id="benefit-amount" inputMode="decimal" placeholder="$0" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <Label>Resets</Label>
                <CadenceSelect value={cadence} onChange={setCadence} />
              </div>
              <div className="col-span-2 grid gap-1.5">
                <Label>Measured from</Label>
                <Segmented
                  value={basis}
                  onChange={(v) => setBasis(v as ResetBasis)}
                  options={[{ value: 'calendar', label: 'Calendar' }, { value: 'cardmember_year', label: 'Card anniversary' }]}
                />
                <p className="text-xs text-gray-500">Calendar resets Jan 1, Apr 1, Jul 1… Card anniversary resets on the day you opened the card.</p>
              </div>
            </div>
          )}
          {kind === 'certificate' && (
            <div className="grid gap-1.5">
              <Label>Arrives</Label>
              <CadenceSelect value={cadence} onChange={setCadence} />
              <p className="text-xs text-gray-500">Certificates like free nights arrive each card anniversary and are used all at once.</p>
            </div>
          )}
          <ToggleRow label="Needs enrollment" detail="Pays out only after you enroll with the issuer." checked={enrollment} onChange={setEnrollment} />
          <ToggleRow label="Remind me" detail="Reminders are sent by the Chequey iOS app." checked={reminders} onChange={setReminders} />
          <div className="grid gap-1.5">
            <Label htmlFor="benefit-detail">Notes</Label>
            <Input id="benefit-detail" placeholder="Where it works, how it posts…" value={detail} onChange={(e) => setDetail(e.target.value)} />
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {benefit ? (
              <Button type="button" variant="ghost" className="text-danger" onClick={remove}>
                <Trash2 className="mr-1.5 h-4 w-4" /> Delete
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function CadenceSelect({ value, onChange }: { value: Cadence; onChange: (v: Cadence) => void }) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Cadence)}>
      <SelectTrigger><SelectValue /></SelectTrigger>
      <SelectContent>
        {CADENCES.map((c) => <SelectItem key={c} value={c}>{CADENCE_LABEL[c]}</SelectItem>)}
      </SelectContent>
    </Select>
  )
}

function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div className="inline-flex w-full rounded-lg bg-gray-100 p-1 dark:bg-gray-800" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            value === o.value ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100' : 'text-gray-500 hover:text-gray-900 dark:hover:text-gray-100'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function ToggleRow({ label, detail, checked, onChange }: { label: string; detail: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</div>
        <div className="text-xs text-gray-500">{detail}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </div>
  )
}

// MARK: - Card form (add or edit)

export interface CardFormTarget {
  card?: PerkCard
  template?: CardTemplate | null
  account?: PerkCreditAccount | null
}

export function CardFormDialog({
  target,
  state,
  onOpenChange,
  onSaved,
}: {
  target: CardFormTarget | null
  state: PerksState | undefined
  onOpenChange: (open: boolean) => void
  onSaved?: (state: PerksState) => void
}) {
  const mutate = usePerksMutation()
  const [name, setName] = useState('')
  const [issuer, setIssuer] = useState('')
  const [opened, setOpened] = useState('')
  const [fee, setFee] = useState('')
  const [account, setAccount] = useState(NONE)
  const [art, setArt] = useState('obsidian')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!target) return
    const { card, template, account: acct } = target
    const yearAgo = new Date()
    yearAgo.setFullYear(yearAgo.getFullYear() - 1)
    setName(card?.name ?? acct?.name ?? '')
    setIssuer(card?.issuer ?? acct?.institution_name ?? '')
    setOpened(card?.opened_on ?? formatDay(yearAgo))
    setFee(String((card?.annual_fee_cents ?? template?.annual_fee_cents ?? 0) / 100))
    // Picked from the catalog: preselect the matching linked account, if there is one.
    const suggested = !card && !acct && template && state ? suggestedAccount(state, template.id) : null
    setAccount(card?.account_id ?? acct?.account_id ?? suggested?.account_id ?? NONE)
    setArt(card?.art ?? 'obsidian')
    // Only when a new target opens; state refreshes must not reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target])

  const accounts = useMemo(
    () => (state?.credit_accounts ?? []).filter((a) => a.card_id === null || a.card_id === target?.card?.id),
    [state, target]
  )

  if (!target) return null
  const { card, template } = target
  const editing = Boolean(card)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const cardName = template?.name ?? name.trim()
    if (!cardName) return toast.error('Give the card a name.')
    if (!opened) return toast.error('Enter when the card was opened.')
    const feeCents = parseCents(fee) ?? 0
    const accountId = account === NONE ? null : account
    setSaving(true)
    try {
      const next = await mutate(() =>
        card
          ? perksApi.updateCard(card.id, { name: cardName, issuer: issuer.trim(), opened_on: opened, annual_fee_cents: feeCents, art, account_id: accountId })
          : perksApi.createCard({
              template_id: template?.id, account_id: accountId, name: cardName,
              issuer: template?.issuer ?? issuer.trim(), opened_on: opened, annual_fee_cents: feeCents,
              art: template?.art ?? art,
            })
      )
      toast.success(card ? 'Card updated' : `${cardName} added`)
      onSaved?.(next)
      onOpenChange(false)
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Card' : template?.name ?? 'New Card'}</DialogTitle>
          {template && !editing && (
            <DialogDescription>
              {plural(template.benefits.length, 'benefit')} worth {money(Math.round(templateCreditValueCents(template) / 100) * 100)} a year {template.benefits.length === 1 ? 'comes' : 'come'} with it. You can edit or remove any of them afterwards.
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {(!template || editing) && (
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="card-name">Card name</Label>
                <Input id="card-name" placeholder="e.g. Bilt Mastercard" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="card-issuer">Issuer</Label>
                <Input id="card-issuer" placeholder="e.g. Wells Fargo" value={issuer} onChange={(e) => setIssuer(e.target.value)} />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="card-opened">Card opened</Label>
              <Input id="card-opened" type="date" value={opened} onChange={(e) => setOpened(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="card-fee">Annual fee</Label>
              <Input id="card-fee" inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />
            </div>
          </div>
          <p className="-mt-2 text-xs text-gray-500">The open date sets your card year: anniversary credits and the annual fee renew on it.</p>
          <div className="grid gap-1.5">
            <Label>Account for statement credits</Label>
            <Select value={account} onValueChange={setAccount}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not linked</SelectItem>
                {accounts.map((a) => (
                  <SelectItem key={a.account_id} value={a.account_id}>{a.name}{a.mask ? ` •• ${a.mask}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              {editing ? 'Changing the account clears credits picked up from the old one.' : 'Link the card’s account and Chequey marks credits as used when they post.'}
            </p>
          </div>
          {(!template || editing) && (
            <div className="grid gap-1.5">
              <Label>Card color</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(CARD_ART).map(([key, style]) => (
                  <button
                    key={key}
                    type="button"
                    title={style.label}
                    aria-label={style.label}
                    aria-pressed={art === key}
                    onClick={() => setArt(key)}
                    className={cn('rounded-md p-0.5 ring-2 ring-offset-1 dark:ring-offset-gray-900', art === key ? 'ring-primary-500' : 'ring-transparent')}
                  >
                    <CardArt art={key} mini />
                  </button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : editing ? 'Save' : 'Add card'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// MARK: - Add card picker

export function AddCardDialog({
  open,
  state,
  onOpenChange,
  onPick,
}: {
  open: boolean
  state: PerksState | undefined
  onOpenChange: (open: boolean) => void
  onPick: (target: CardFormTarget) => void
}) {
  const { data: catalog, isLoading } = usePerksCatalog()
  const [account, setAccount] = useState<PerkCreditAccount | null>(null)
  const change = (next: boolean) => {
    if (!next) setAccount(null)
    onOpenChange(next)
  }
  const unlinked = (state?.credit_accounts ?? []).filter((a) => a.card_id === null)
  const template = (id: string | null) => catalog?.templates.find((t) => t.id === id) ?? null

  const pick = (target: CardFormTarget) => {
    change(false)
    onPick(target)
  }

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{account ? `Which card is ${account.name}?` : 'Add a Card'}</DialogTitle>
          <DialogDescription>
            {account ? 'Pick its card type to load its benefits.' : 'Start from a linked card account, a known card, or a blank card.'}
          </DialogDescription>
        </DialogHeader>

        {!account && unlinked.length > 0 && (
          <section>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">Your linked cards</div>
            {unlinked.map((a) => {
              const t = template(a.suggested_template_id)
              return (
                <PickerRow
                  key={a.account_id}
                  leading={<div className="flex h-[22px] w-[34px] items-center justify-center rounded bg-gray-100 dark:bg-gray-800"><CreditCard className="h-3.5 w-3.5 text-gray-500" /></div>}
                  title={a.name}
                  subtitle={[a.mask && `•• ${a.mask}`, t ? `${t.name} · ${plural(t.benefits.length, 'benefit')}` : 'Pick the card type next'].filter(Boolean).join(' · ')}
                  onClick={() => (t ? pick({ template: t, account: a }) : setAccount(a))}
                />
              )
            })}
          </section>
        )}

        <section>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">{account ? 'Card types' : 'Popular cards'}</div>
          {isLoading && <p className="py-4 text-sm text-gray-500">Loading cards…</p>}
          {catalog?.templates.map((t) => (
            <PickerRow
              key={t.id}
              leading={<CardArt art={t.art} mini />}
              title={t.name}
              subtitle={`${t.issuer} · ${money(t.annual_fee_cents)} fee`}
              trailing={
                <div className="text-right">
                  <div className="font-medium tabular-nums text-success">{money(Math.round(templateCreditValueCents(t) / 100) * 100)}</div>
                  <div className="text-xs text-gray-400">credits / yr</div>
                </div>
              }
              onClick={() => pick({ template: t, account })}
            />
          ))}
          <PickerRow
            leading={<div className="flex h-[22px] w-[34px] items-center justify-center rounded border border-dashed border-gray-300 dark:border-gray-700"><CreditCard className="h-3.5 w-3.5 text-gray-400" /></div>}
            title={account ? 'Not listed' : 'Another card'}
            subtitle="Start blank and add its credits yourself"
            onClick={() => pick({ template: null, account })}
          />
        </section>
      </DialogContent>
    </Dialog>
  )
}

function PickerRow({ leading, title, subtitle, trailing, onClick }: { leading: React.ReactNode; title: string; subtitle: string; trailing?: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-4 rounded-lg px-2 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60">
      {leading}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-gray-900 dark:text-gray-100">{title}</div>
        <div className="truncate text-sm text-gray-500">{subtitle}</div>
      </div>
      {trailing}
    </button>
  )
}

// MARK: - Statement credits

export function AssignCreditDialog({
  credit,
  state,
  onOpenChange,
}: {
  credit: PerkUnmatchedCredit | null
  state: PerksState | undefined
  onOpenChange: (open: boolean) => void
}) {
  const mutate = usePerksMutation()
  if (!credit) return null
  const card = state?.cards.find((c) => c.id === credit.card_id)
  const run = async (call: () => Promise<{ data: PerksState }>, done: string) => {
    try {
      await mutate(call)
      toast.success(done)
      onOpenChange(false)
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{credit.name}</DialogTitle>
          <DialogDescription>
            +{money(credit.amount_cents)} on {card?.name ?? 'your card'}, {longDay(credit.date)}. Which benefit is it? Chequey will recognise the next one by itself.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1">
          {(card?.benefits ?? []).filter((b) => b.kind === 'credit').map((b) => (
            <Button key={b.id} variant="ghost" className="justify-start" onClick={() => run(() => perksApi.assignCredit(credit.transaction_id, b.id), `Counted toward ${b.name}`)}>
              {b.name}
            </Button>
          ))}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" className="text-danger" onClick={() => run(() => perksApi.ignoreCredit(credit.transaction_id), 'Hidden')}>
            Not a card benefit
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// MARK: - Enrollment

export function EnrollmentDialog({ open, state, onOpenChange }: { open: boolean; state: PerksState | undefined; onOpenChange: (open: boolean) => void }) {
  const mutate = usePerksMutation()
  const pending = (state?.cards ?? []).flatMap((card) => card.benefits.filter((b) => b.requires_enrollment && !b.is_enrolled).map((benefit) => ({ card, benefit })))
  const enroll = async (benefit: PerkBenefit) => {
    try {
      await mutate(() => perksApi.updateBenefit(benefit.id, { is_enrolled: true }))
      toast.success(`${benefit.name} marked as enrolled`)
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enrollment</DialogTitle>
          <DialogDescription>These only pay out after you enroll in the issuer’s app or website. Mark each one once you have.</DialogDescription>
        </DialogHeader>
        {pending.length === 0 && <p className="text-sm text-gray-500">Everything is enrolled.</p>}
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {pending.map(({ card, benefit }) => (
            <li key={benefit.id} className="flex items-center gap-3 py-2.5">
              <CardArt art={card.art} mini />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-gray-900 dark:text-gray-100">{benefit.name}</div>
                <div className="text-sm text-gray-500">{card.name}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => enroll(benefit)}>Enrolled</Button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}
